import { useLayoutEffect, useRef } from 'react';
import { reducedMotion } from '../../lib/motion.js';

/** A persistent surface per track; observe buttons too, including cold font loads. */
export function useNavigationIndicators(shell, pathname, onIcon) {
  const sync = useRef(null);
  useLayoutEffect(() => {
    const root = shell.current;
    const indicators = new Map();
    const reduced = reducedMotion;
  function syncIndicators(animate = true) {
    const tracks = [
      ...root.querySelectorAll('.tab-nav'),
      root.querySelector('.navigation-dock'),
      root.querySelector('.navigation-modules'),
    ].filter(track => track && track.getBoundingClientRect().width > 0);
    indicators.forEach((entry, track) => {
      if (!tracks.includes(track)) { entry.observer?.disconnect(); entry.element.remove(); track.classList.remove('navigation-has-indicator'); indicators.delete(track); }
    });
    onIcon(root.querySelector('.tab-btn.active .material-symbols-rounded')?.textContent || null);
    for (const track of tracks) {
      const target = track.querySelector('.tab-btn.active, .navigation-link.active, a[aria-current="page"], button[aria-current="page"]');
      if (!target) {
        indicators.get(track)?.observer?.disconnect();
        indicators.get(track)?.element.remove();
        indicators.delete(track); track.classList.remove('navigation-has-indicator');
        continue;
      }
      let entry = indicators.get(track);
      const moving = entry?.element.getAnimations()[0];
      const oldRect = entry?.element.getBoundingClientRect();
      const bounds = track.getBoundingClientRect();
      let box = target.getBoundingClientRect();
      if (track.classList.contains('tab-nav') && (box.left < bounds.left || box.right > bounds.right)) {
        track.scrollTo({ left: track.scrollLeft + box.left - bounds.left - 8, behavior: 'instant' });
        box = target.getBoundingClientRect();
      }
      const next = {
        left: box.left - bounds.left + track.scrollLeft - track.clientLeft,
        top: box.top - bounds.top + track.scrollTop - track.clientTop,
        width: box.width, height: box.height,
      };
      if (!entry) {
        const element = document.createElement('span');
        element.className = 'navigation-selection-indicator'; element.setAttribute('aria-hidden', 'true');
        track.append(element); track.classList.add('navigation-has-indicator');
        entry = { element, key: null, buttons: new Set(), observer: new ResizeObserver(() => syncIndicators(false)) };
        indicators.set(track, entry);
        entry.observer.observe(track);
      }
      // Observe each track and its targets: rail width transitions, responsive
      // dock mounts and late fonts must all update the indicator geometry.
      if (entry.observer) {
        const buttons = new Set(track.querySelectorAll('.tab-btn,.navigation-link,:scope > a,:scope > button'));
        entry.buttons.forEach(button => { if (!buttons.has(button)) entry.observer.unobserve(button); });
        buttons.forEach(button => { if (!entry.buttons.has(button)) entry.observer.observe(button); });
        entry.buttons = buttons;
      }
      const key = target.getAttribute('href') || target.textContent;
      const shouldMove = animate && !reduced() && entry.key && entry.key !== key && oldRect;
      const previous = oldRect ? {
        left: oldRect.left - bounds.left + track.scrollLeft - track.clientLeft,
        top: oldRect.top - bounds.top + track.scrollTop - track.clientTop,
        width: oldRect.width, height: oldRect.height,
      } : next;
      const geometryChanged = entry.geometry && Object.keys(next).some(name => Math.abs(entry.geometry[name] - next[name]) > .5);
      // Showing/hiding module tabs also resizes the header. Do not cancel a
      // sidebar/dock animation unless that track's destination actually moved.
      if (moving && entry.key === key && !geometryChanged) continue;
      moving?.cancel();
      const pixels = rect => Object.fromEntries(Object.entries(rect).map(([name, value]) => [name, `${value}px`]));
      Object.assign(entry.element.style, pixels(next));
      if (shouldMove) {
        const isTab = track.classList.contains('tab-nav');
        const frames = [pixels(previous), pixels(next)];
        if (!isTab) {
          const union = {
            left: Math.min(previous.left, next.left), top: Math.min(previous.top, next.top),
            width: Math.max(previous.left + previous.width, next.left + next.width) - Math.min(previous.left, next.left),
            height: Math.max(previous.top + previous.height, next.top + next.height) - Math.min(previous.top, next.top),
          };
          frames[0].easing = 'cubic-bezier(.2,0,0,1)';
          frames.splice(1, 0, { ...pixels(union), offset: .48, easing: 'cubic-bezier(.2,0,0,1)' });
        }
        entry.element.animate(frames, {
          duration: (isTab ? 200 : 320),
          easing: isTab ? 'cubic-bezier(.2,0,0,1)' : 'linear',
        });
      }
      entry.key = key;
      entry.geometry = next;
    }
  }

    sync.current = syncIndicators;
    let frame;
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => syncIndicators()); };
    const changes = new MutationObserver(records => {
      if (records.some(record => record.type === 'attributes'
        ? record.target.matches('.tab-btn,.navigation-link,.navigation-dock > a,.navigation-dock > button')
        : [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && !node.classList.contains('navigation-selection-indicator')))) schedule();
    });
    changes.observe(root, { subtree:true, childList:true, attributes:true, attributeFilter:['class','aria-current'] });
    const sizes = new ResizeObserver(() => syncIndicators(false));
    root.querySelectorAll('.navigation-topbar,.navigation-main').forEach(node => sizes.observe(node));
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const cancel = () => { if (media.matches) indicators.forEach(({element}) => element.getAnimations().forEach(animation => animation.cancel())); };
    media.addEventListener('change', cancel);
    let disposed = false;
    const fonts = () => { if (!disposed) syncIndicators(false); };
    document.fonts.ready.then(fonts);
    document.fonts.addEventListener('loadingdone', fonts);
    syncIndicators(false);
    return () => {
      disposed = true; sync.current = null;
      cancelAnimationFrame(frame); changes.disconnect(); sizes.disconnect();
      media.removeEventListener('change', cancel); document.fonts.removeEventListener('loadingdone', fonts);
      indicators.forEach(({element, observer}, track) => { observer?.disconnect(); element.remove(); track.classList.remove('navigation-has-indicator'); });
    };
  }, [shell, onIcon]);
  useLayoutEffect(() => { sync.current?.(); }, [pathname]);
}
